import { isRejectedWithValue, type Middleware } from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'

type ApiErrorShape = { status: number; code: string; message: string }

function isApiError(payload: unknown): payload is ApiErrorShape {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'status' in payload &&
    'code' in payload &&
    typeof (payload as { code: unknown }).code === 'string'
  )
}

// Baseline fetch errors (network down, 5xx, timeout) не бросают exception —
// RTK baseQuery возвращает {error} как value, UI рисует "Не удалось загрузить X",
// но Sentry ничего не видит. Middleware закрывает gap.
//
// AUTH_EXPIRED skip — это expected refresh-fail flow (см. api/client.ts:90),
// пользователь редиректится на login, не bug. Всё остальное летит в Sentry
// с тегами endpoint/status/code для фильтрации в UI.
export const sentryRtkMiddleware: Middleware = () => (next) => (action) => {
  if (!isRejectedWithValue(action)) return next(action)

  try {
    const payload = action.payload
    if (isApiError(payload) && payload.code === 'AUTH_EXPIRED') {
      return next(action)
    }

    const meta = (action as { meta?: { arg?: { endpointName?: string; type?: string } } }).meta
    const endpoint = meta?.arg?.endpointName ?? 'unknown'
    const endpointType = meta?.arg?.type ?? 'query'
    const status = isApiError(payload) ? String(payload.status) : 'unknown'
    const code = isApiError(payload) ? payload.code : 'UNKNOWN'
    const message = isApiError(payload) ? payload.message : 'RTK rejection without ApiError payload'

    Sentry.captureException(new Error(`RTK ${endpointType} ${endpoint} failed: ${message}`), {
      tags: {
        'rtk.endpoint': endpoint,
        'rtk.type': endpointType,
        'rtk.status': status,
        'rtk.code': code,
      },
      extra: { payload, meta },
      // Fingerprint по endpoint+code — иначе Sentry сгруппирует всё по stack trace
      // middleware'а и мы потеряем разделение по эндпоинтам.
      fingerprint: ['rtk-rejection', endpoint, code],
    })
  } catch {
    // Middleware не имеет права ронять dispatch. Если Sentry сломан — глотаем.
  }

  return next(action)
}
